/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { vendorCRMService } from '../../../services/VendorCRMService';
import VendorDetail from './VendorDetail';
import PremiumFeatureGate from '../PremiumFeatureGate';
import AIService from '../../../services/AIService';
import {
  SearchIcon,
  StarIcon,
  BanIcon,
  RefreshIcon,
  UserGroupIcon,
  FilterIcon,
  XIcon,
  DownloadIcon,
  SparklesIcon,
  ChevronDownIcon,
  PlusIcon,
  MailIcon
} from '@heroicons/react/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/solid';

// Compute top earners + regulars for badges
const getVendorBadge = (vendor, allVendors) => {
  if (vendor.isBlocked) return null;
  const sorted = [...allVendors]
    .filter(v => !v.isBlocked && (v.totalRevenue || 0) > 0)
    .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0));
  const topThree = sorted.slice(0, 3).map(v => v.id);
  if (topThree.includes(vendor.id)) return 'top';
  if ((vendor.eventsAttended || 0) >= 3) return 'regular';
  return null;
};

// Quick invite modal — opens directly from the vendor list row
const QuickInviteModal = ({ vendor, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const generate = async () => {
      try {
        const result = await AIService.generateVendorInvite(vendor, []);
        setSubject(result.subject);
        setBody(result.body);
      } catch (err) {
        console.error('Invite generation error:', err);
      } finally {
        setLoading(false);
      }
    };
    generate();
  }, [vendor]);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenInMail = () => {
    window.open(
      `mailto:${vendor.businessEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    );
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col">

          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {vendor.companyLogoURL ? (
                <img src={vendor.companyLogoURL} alt={vendor.businessName}
                  className="w-10 h-10 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-primary-700 font-semibold text-sm">
                    {vendor.businessName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              <div>
                <p className="font-semibold text-gray-900 text-sm leading-tight">{vendor.businessName}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {vendor.eventsAttended > 0
                    ? `${vendor.eventsAttended} event${vendor.eventsAttended !== 1 ? 's' : ''} attended`
                    : vendor.businessType || 'Vendor'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                <SparklesIcon className="w-3 h-3" />
                AI Draft
              </span>
              <button onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          {loading ? (
            <div className="px-5 py-12 flex flex-col items-center gap-3">
              <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-blue-600 animate-spin" />
              <p className="text-sm text-gray-400">Drafting personalized invite…</p>
            </div>
          ) : (
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[10px] text-gray-400 uppercase tracking-wide mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 uppercase tracking-wide mb-1">Body</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none leading-relaxed"
                />
              </div>
            </div>
          )}

          {/* Footer */}
          {!loading && (
            <div className="px-5 py-3.5 border-t border-gray-100 flex items-center gap-2 justify-end">
              <button onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition">
                Close
              </button>
              <button onClick={handleCopy}
                className="px-4 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleOpenInMail}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                <MailIcon className="w-3.5 h-3.5" />
                Open in Mail
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

const VendorCRMContent = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { synced, total, ts }
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [inviteVendor, setInviteVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'favorites' | 'blocked'
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [syncInsights, setSyncInsights] = useState([]);

  const businessTypes = [
    'Food & Beverage', 'Arts & Crafts', 'Clothing & Apparel',
    'Health & Wellness', 'Home & Garden', 'Jewelry & Accessories',
    'Services', 'Entertainment', 'Other'
  ];

  const fetchVendors = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const [vendorsResult, statsResult, tagsResult] = await Promise.all([
        vendorCRMService.getVendorDirectory(user.uid),
        vendorCRMService.getVendorStats(user.uid),
        vendorCRMService.getAllTags(user.uid)
      ]);
      if (vendorsResult.success) {
        setVendors(vendorsResult.vendors);
      }
      if (statsResult.success) setStats(statsResult.stats);
      if (tagsResult.success) setAllTags(tagsResult.tags);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // Apply filters whenever deps change
  useEffect(() => {
    let result = [...vendors];

    // Tab filter
    if (activeTab === 'favorites') result = result.filter(v => v.isFavorite);
    else if (activeTab === 'blocked') result = result.filter(v => v.isBlocked);
    else result = result.filter(v => !v.isBlocked); // "All" hides blocked

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(v =>
        v.businessName?.toLowerCase().includes(term) ||
        v.businessEmail?.toLowerCase().includes(term) ||
        v.fullName?.toLowerCase().includes(term)
      );
    }
    if (businessTypeFilter) result = result.filter(v => v.businessType === businessTypeFilter);
    if (tagFilter) result = result.filter(v => v.tags?.includes(tagFilter));

    setFilteredVendors(result);
    setSelectedIds(new Set()); // clear selection on filter change
  }, [vendors, searchTerm, activeTab, businessTypeFilter, tagFilter]);

  const handleSync = async () => {
    if (!user?.uid) return;
    setSyncing(true);
    try {
      const result = await vendorCRMService.syncVendorsFromApplications(user.uid);
      if (result.success) {
        await fetchVendors();
        setSyncResult({ synced: result.synced || 0, ts: new Date() });

        // Run AI features after sync — use the latest vendors from state
        // (fetchVendors updates the vendors state; we read it after the next render via a fresh call)
        try {
          const [vendorsResult] = await Promise.all([
            vendorCRMService.getVendorDirectory(user.uid),
          ]);
          const updatedVendors = vendorsResult.success ? vendorsResult.vendors : [];

          const [suggestions, insights] = await Promise.all([
            AIService.suggestTags(updatedVendors),
            AIService.getSyncInsights(updatedVendors),
          ]);

          if (suggestions.length > 0) {
            setTagSuggestions(suggestions);
            setShowTagSuggestions(true);
          }
          setSyncInsights(insights);
        } catch (aiErr) {
          console.error('AI post-sync error:', aiErr);
        }
      }
    } catch (error) {
      console.error('Error syncing vendors:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleApplyTagSuggestion = async (suggestion) => {
    if (!user?.uid) return;
    await Promise.all(
      suggestion.vendorIds.map((vendorId) =>
        vendorCRMService.addTag(user.uid, vendorId, suggestion.tag)
      )
    );
    setVendors((prev) =>
      prev.map((v) =>
        suggestion.vendorIds.includes(v.id)
          ? { ...v, tags: [...new Set([...(v.tags || []), suggestion.tag])] }
          : v
      )
    );
    setTagSuggestions((prev) => prev.filter((s) => s.tag !== suggestion.tag));
    if (tagSuggestions.length <= 1) setShowTagSuggestions(false);
  };

  const handleApplyAllTagSuggestions = async () => {
    if (!user?.uid) return;
    await Promise.all(
      tagSuggestions.flatMap((suggestion) =>
        suggestion.vendorIds.map((vendorId) =>
          vendorCRMService.addTag(user.uid, vendorId, suggestion.tag)
        )
      )
    );
    setVendors((prev) =>
      prev.map((v) => {
        const tagsToAdd = tagSuggestions
          .filter((s) => s.vendorIds.includes(v.id))
          .map((s) => s.tag);
        return tagsToAdd.length > 0
          ? { ...v, tags: [...new Set([...(v.tags || []), ...tagsToAdd])] }
          : v;
      })
    );
    setTagSuggestions([]);
    setShowTagSuggestions(false);
  };

  const handleToggleFavorite = async (vendorId, currentStatus, e) => {
    e?.stopPropagation();
    if (!user?.uid) return;
    const result = await vendorCRMService.toggleFavorite(user.uid, vendorId, !currentStatus);
    if (result.success) {
      setVendors(prev => prev.map(v => v.id === vendorId ? { ...v, isFavorite: !currentStatus } : v));
    }
  };

  const handleToggleBlocked = async (vendorId, currentStatus, e) => {
    e?.stopPropagation();
    if (!user?.uid) return;
    const result = await vendorCRMService.toggleBlocked(user.uid, vendorId, !currentStatus);
    if (result.success) {
      setVendors(prev => prev.map(v =>
        v.id === vendorId ? { ...v, isBlocked: !currentStatus, status: !currentStatus ? 'blocked' : 'active' } : v
      ));
    }
  };

  const handleViewVendor = (vendor) => {
    setSelectedVendor(vendor);
    setShowDetail(true);
  };

  // Bulk actions
  const toggleSelectVendor = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === filteredVendors.length
      ? new Set()
      : new Set(filteredVendors.map(v => v.id))
    );
  };
  const handleBulkFavorite = async () => {
    const sel = filteredVendors.filter(v => selectedIds.has(v.id));
    const allFav = sel.every(v => v.isFavorite);
    await Promise.all(sel.map(v => vendorCRMService.toggleFavorite(user.uid, v.id, !allFav)));
    setVendors(prev => prev.map(v => selectedIds.has(v.id) ? { ...v, isFavorite: !allFav } : v));
  };
  const handleBulkBlock = async () => {
    const sel = filteredVendors.filter(v => selectedIds.has(v.id));
    const allBlocked = sel.every(v => v.isBlocked);
    await Promise.all(sel.map(v => vendorCRMService.toggleBlocked(user.uid, v.id, !allBlocked)));
    setVendors(prev => prev.map(v =>
      selectedIds.has(v.id) ? { ...v, isBlocked: !allBlocked, status: !allBlocked ? 'blocked' : 'active' } : v
    ));
    setSelectedIds(new Set());
  };
  const handleExportCSV = () => {
    const toExport = selectedIds.size > 0
      ? filteredVendors.filter(v => selectedIds.has(v.id))
      : filteredVendors;
    const headers = ['Business Name', 'Contact Name', 'Email', 'Phone', 'Category', 'Events Attended', 'Total Revenue', 'Tags', 'Status'];
    const rows = toExport.map(v => [
      v.businessName || '', v.fullName || '', v.businessEmail || '',
      v.businessPhone || '', v.businessType || '',
      v.eventsAttended || 0,
      v.totalRevenue ? (v.totalRevenue / 100).toFixed(2) : '0.00',
      (v.tags || []).join('; '),
      v.isBlocked ? 'Blocked' : v.isFavorite ? 'Favorite' : 'Active'
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `vendors-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = [businessTypeFilter, tagFilter].filter(Boolean).length;
  const hasVendors = vendors.length > 0;

  const tabs = [
    { id: 'all', label: t('common.all'), count: vendors.filter(v => !v.isBlocked).length },
    { id: 'favorites', label: t('vendorCRM.favorites'), count: vendors.filter(v => v.isFavorite).length },
    { id: 'blocked', label: t('vendorCRM.blocked'), count: vendors.filter(v => v.isBlocked).length },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('vendorCRM.title')}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{t('vendorCRM.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasVendors && (
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition"
            >
              <DownloadIcon className="w-4 h-4" />
              {t('common.export')}
            </button>
          )}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
          >
            <RefreshIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? t('vendorCRM.syncing') : t('vendorCRM.syncFromEvents')}
          </button>
        </div>
      </div>

      {/* Sync result feedback */}
      {syncResult && (
        <div className="mb-4 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span>
              {t('vendorCRM.syncComplete')} —{' '}
              <span className="font-medium text-gray-900">
                {syncResult.synced === 0 ? t('vendorCRM.noNewVendors') : t('vendorCRM.newVendorsAdded', { count: syncResult.synced })}
              </span>
              {' · '}{vendors.filter(v => !v.isBlocked).length} {t('vendorCRM.totalInDirectory')}
            </span>
            <button onClick={() => setSyncResult(null)} className="text-gray-400 hover:text-gray-600">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          {syncInsights.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {syncInsights.map((insight, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <SparklesIcon className="w-3 h-3" /> {insight}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tag suggestions — shown after sync if AI found patterns */}
      {showTagSuggestions && tagSuggestions.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-800">{t('vendorCRM.tagSuggestions')}</span>
            </div>
            <button onClick={() => setShowTagSuggestions(false)} className="text-gray-400 hover:text-gray-600">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {tagSuggestions.map(s => (
              <button key={s.tag} onClick={() => handleApplyTagSuggestion(s)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-gray-400 transition">
                <PlusIcon className="w-3.5 h-3.5" />
                {t('vendorCRM.tagVendors', { count: s.count, tag: s.tag })}
              </button>
            ))}
            <button onClick={() => handleApplyAllTagSuggestions()}
              className="px-3 py-1.5 bg-gray-900 text-white text-sm rounded-full hover:bg-gray-800 transition">
              {t('common.applyAll')}
            </button>
          </div>
        </div>
      )}

      {/* Onboarding banner — only show when no vendors yet and no sync result */}
      {!hasVendors && !loading && !syncResult && (
        <div className="mb-6 p-5 bg-gray-900 rounded-xl text-white">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
              <UserGroupIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-1">{t('vendorCRM.buildDirectory')}</h3>
              <p className="text-sm text-gray-300 mb-3">
                {t('vendorCRM.buildDirectoryDesc')}
              </p>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                <RefreshIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('vendorCRM.syncing') : t('vendorCRM.syncNow')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats cards — clickable as filters */}
      {stats && hasVendors && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: t('vendorCRM.totalVendors'), value: stats.total,
              icon: <UserGroupIcon className="w-4 h-4 text-gray-500" />,
              tab: 'all'
            },
            {
              label: t('vendorCRM.favorites'), value: stats.favorites,
              icon: <StarSolidIcon className="w-4 h-4 text-yellow-500" />,
              tab: 'favorites'
            },
            {
              label: t('vendorCRM.blocked'), value: stats.blocked,
              icon: <BanIcon className="w-4 h-4 text-red-400" />,
              tab: 'blocked'
            },
            {
              label: t('vendorCRM.totalRevenue'),
              value: `$${((stats.totalRevenue || 0) / 100).toLocaleString()}`,
              icon: <span className="text-green-600 font-bold text-xs">$</span>,
              tab: null
            },
          ].map(card => (
            <button
              key={card.label}
              onClick={() => card.tab && setActiveTab(card.tab)}
              className={`bg-white rounded-xl p-4 border text-left transition ${
                card.tab
                  ? 'border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer'
                  : 'border-gray-200 cursor-default'
              } ${activeTab === card.tab && card.tab ? 'border-gray-400 ring-1 ring-gray-300' : ''}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 bg-white border border-gray-100 rounded-md flex items-center justify-center">
                  {card.icon}
                </div>
                {card.tab && (
                  <span className="text-[10px] text-gray-400 ml-auto">{t('vendorCRM.filterUp')}</span>
                )}
              </div>
              <p className="text-xl font-semibold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Main table card */}
      {hasVendors && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

          {/* Tabs + search + filter row */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between px-4 pt-3 gap-3 flex-wrap">
              {/* Tabs */}
              <div className="flex gap-0">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-gray-900 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`ml-2 px-1.5 py-0.5 text-[10px] rounded-full ${
                        activeTab === tab.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search + filter */}
              <div className="flex items-center gap-2 pb-1">
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('vendorCRM.searchVendors')}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 w-48"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition ${
                    activeFilterCount > 0
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FilterIcon className="w-4 h-4" />
                  {activeFilterCount > 0 ? `${activeFilterCount} ${activeFilterCount > 1 ? t('common.filters') : t('common.filter')}` : t('common.filter')}
                </button>
              </div>
            </div>

            {/* Expanded filters */}
            {showFilters && (
              <div className="px-4 pb-3 pt-2 border-t border-gray-100 flex flex-wrap gap-2 items-center">
                <select
                  value={businessTypeFilter}
                  onChange={e => setBusinessTypeFilter(e.target.value)}
                  className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                >
                  <option value="">{t('vendorCRM.allCategories')}</option>
                  {businessTypes.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                </select>
                {allTags.length > 0 && (
                  <select
                    value={tagFilter}
                    onChange={e => setTagFilter(e.target.value)}
                    className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                  >
                    <option value="">{t('vendorCRM.allTags')}</option>
                    {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                  </select>
                )}
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => { setBusinessTypeFilter(''); setTagFilter(''); }}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
                  >
                    <XIcon className="w-3.5 h-3.5" /> {t('common.clearAll')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">{t('vendorCRM.selected', { count: selectedIds.size })}</span>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-400 hover:text-gray-700 underline">{t('vendorCRM.deselect')}</button>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={handleBulkFavorite} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition">
                  <StarSolidIcon className="w-3.5 h-3.5 text-yellow-500" /> {t('vendorCRM.favorites')}
                </button>
                <button onClick={handleBulkBlock} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition">
                  <BanIcon className="w-3.5 h-3.5 text-red-400" /> {t('vendorCRM.blocked')}
                </button>
                <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition">
                  <DownloadIcon className="w-3.5 h-3.5 text-gray-500" /> {t('common.export')}
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-gray-900 animate-spin mx-auto" />
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="p-12 text-center">
              <UserGroupIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {searchTerm || activeFilterCount > 0
                  ? t('vendorCRM.noVendorsMatch')
                  : activeTab === 'favorites' ? t('vendorCRM.noFavoritesYet')
                  : activeTab === 'blocked' ? t('vendorCRM.noBlockedVendors')
                  : t('vendorCRM.noVendorsInView')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={filteredVendors.length > 0 && selectedIds.size === filteredVendors.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('vendorCRM.vendor')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden sm:table-cell">{t('vendorCRM.category')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('vendorCRM.eventsAttended')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden md:table-cell">{t('vendorCRM.tags')}</th>
                    <th className="px-4 py-3 w-40"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredVendors.map((vendor) => {
                    const badge = getVendorBadge(vendor, vendors);
                    return (
                      <tr
                        key={vendor.id}
                        className="hover:bg-gray-50 cursor-pointer transition"
                        onClick={() => handleViewVendor(vendor)}
                      >
                        <td className="px-4 py-3.5 w-8" onClick={e => toggleSelectVendor(vendor.id, e)}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(vendor.id)}
                            onChange={() => {}}
                            className="rounded border-gray-300"
                          />
                        </td>

                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            {vendor.companyLogoURL ? (
                              <img src={vendor.companyLogoURL} alt={vendor.businessName}
                                className="w-9 h-9 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                                <span className="text-gray-500 font-medium text-sm">
                                  {vendor.businessName?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-gray-900 text-sm truncate">{vendor.businessName}</p>
                                {badge === 'top' && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full border border-amber-200 shrink-0">
                                    ★ {t('vendorCRM.topEarner')}
                                  </span>
                                )}
                                {badge === 'regular' && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded-full shrink-0">
                                    ↩ {t('vendorCRM.regular')}
                                  </span>
                                )}
                                {vendor.isFavorite && (
                                  <StarSolidIcon className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-gray-400 truncate">{vendor.businessEmail}</p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3.5 hidden sm:table-cell">
                          <span className="text-sm text-gray-500">{vendor.businessType || '—'}</span>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className="text-sm text-gray-700 font-medium">{vendor.eventsAttended || 0}</span>
                        </td>

                        <td className="px-4 py-3.5 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {vendor.tags?.slice(0, 2).map(tag => (
                              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                            {vendor.tags?.length > 2 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-xs rounded-full">
                                +{vendor.tags.length - 2}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3.5 w-40">
                          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                            {!vendor.isBlocked && (
                              <button
                                onClick={e => { e.stopPropagation(); setInviteVendor(vendor); }}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shrink-0"
                                title="Draft invite email"
                              >
                                <MailIcon className="w-3.5 h-3.5" />
                                Invite
                              </button>
                            )}
                            <button
                              onClick={e => handleToggleFavorite(vendor.id, vendor.isFavorite, e)}
                              className={`p-1.5 rounded-lg transition ${
                                vendor.isFavorite ? 'text-yellow-400' : 'text-gray-300 hover:text-gray-500'
                              }`}
                              title={vendor.isFavorite ? t('vendorCRM.removeFavorite') : t('vendorCRM.markFavorite')}
                            >
                              {vendor.isFavorite
                                ? <StarSolidIcon className="w-4 h-4" />
                                : <StarIcon className="w-4 h-4" />
                              }
                            </button>
                            <button
                              onClick={e => handleToggleBlocked(vendor.id, vendor.isBlocked, e)}
                              className={`p-1.5 rounded-lg transition ${
                                vendor.isBlocked ? 'text-red-400' : 'text-gray-300 hover:text-red-400'
                              }`}
                              title={vendor.isBlocked ? t('vendorCRM.unblock') : t('vendorCRM.blockVendor')}
                            >
                              <BanIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vendor detail panel */}
      {showDetail && selectedVendor && (
        <VendorDetail
          vendor={selectedVendor}
          allVendors={vendors}
          onClose={() => { setShowDetail(false); setSelectedVendor(null); }}
          onUpdate={fetchVendors}
        />
      )}

      {/* Quick invite modal */}
      {inviteVendor && (
        <QuickInviteModal
          vendor={inviteVendor}
          onClose={() => setInviteVendor(null)}
        />
      )}
    </div>
  );
};

const VendorCRM = () => (
  <PremiumFeatureGate featureName="Vendor CRM">
    <VendorCRMContent />
  </PremiumFeatureGate>
);

export default VendorCRM;
